import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";

import { useDispatch, useSelector } from "react-redux";
import useLoading from "./useLoading";
import {
  setListing,
  setListingAll,
  setNextToken,
  setSearchText,
  setSelected,
} from "../store/ducks/invoiceItemsCode";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
// Assuming these queries/mutations will be generated by Amplify codegen
// If not, the user will need to run 'amplify api gql-compile' or 'amplify push'
// I'll use 'any' type for now to avoid TS errors on missing types
import { listInvoiceItemsCodes, getInvoiceItemsCode } from "../graphql/queries";
import { createInvoiceItemsCode, updateInvoiceItemsCode, deleteInvoiceItemsCode } from "../graphql/mutations";
import { ListingVariables } from "../models/app";

const useInvoiceItemsCode = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { session, showConfirm, showError } = useApp();
  const { changeLoading } = useLoading();
  const listing = useSelector(
    (state: any) => state[`${listingName}`]["listing"]
  );
  const listingAll = useSelector(
    (state: any) => state[`${listingName}`]["listingAll"]
  );

  const nextToken = useSelector(
    (state: any) => state[`${listingName}`]["nextToken"]
  );

  const searchText = useSelector(
    (state: any) => state[`${listingName}`]["searchText"]
  );
  const selected = useSelector(
    (state: any) => state[`${listingName}`]["selected"]
  );

  async function fetch(params: ListingVariables) {
    try {
      const { startIndex, limit, session } = params;

      const filter: any = {
        deleted: { eq: "0" },
      };

      if (searchText) filter.invoiceCode = { contains: searchText.toLowerCase() };

      const result: any = await API.graphql({
        query: listInvoiceItemsCodes,
        variables: { filter, limit, nextToken: nextToken },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const currentNextToken = result.data.listInvoiceItemsCodes.nextToken;
      const listing = result.data.listInvoiceItemsCodes.items;

      dispatch(setListing(listing));
      dispatch(setNextToken(currentNextToken));

      return listing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err.message);
    }
  }

  async function fetchAll(params: ListingVariables) {
    try {
      const { session } = params;

      const filter: any = {
        deleted: { eq: "0" },
      };

      const result: any = await API.graphql({
        query: listInvoiceItemsCodes,
        variables: { filter },
        authMode: session
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      const listing = (result.data.listInvoiceItemsCodes.items || []).filter((item: any) => item !== null);

      dispatch(setListingAll(listing));

      return listing;
    } catch (err: Error | any) {
      if (err?.data?.listInvoiceItemsCodes) {
        const listing = (err.data.listInvoiceItemsCodes.items || []).filter((item: any) => item !== null);
        dispatch(setListingAll(listing));
        return listing;
      }
      console.log(err);
      showError(err.message);
    }
  }

  async function get(id: string) {
    try {
      let single: any | undefined;
      if (listing.length !== 0) {
        single = listing.find((resource: any) => resource.id === id);
      }

      if (single === undefined) {
        const result: any = await API.graphql({
          query: getInvoiceItemsCode,
          variables: { id },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
        single = result.data.getInvoiceItemsCode;
      }

      return single;
    } catch (err) {
      showError(err);
    }
  }

  async function create(data: any) {
    try {
      const createInput: any = {
        operaCode: data.operaCode,
        invoiceCode: data.invoiceCode,
        name: data.name,
        vats: data.vats
          ? String(data.vats)
              .split(",")
              .map((v: string) => parseFloat(v.trim()))
              .filter((n) => !isNaN(n))
          : [],
        price: data.price ? parseFloat(data.price) : null,
        deleted: "0",
        createdAt: new Date().getTime().toString(),
        createdByID: session.sub,
        createdByName: session.name,
      };

      await API.graphql({
        query: createInvoiceItemsCode,
        variables: { input: createInput },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      showConfirm(`New ${singleName} has been created successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function update(id: string, data: any) {
    try {
      const original = await get(id);

      if (original) {
        const updateInput: any = {
          id: original.id,
          operaCode: data.operaCode ? data.operaCode : original.operaCode,
          invoiceCode: data.invoiceCode
            ? data.invoiceCode
            : original.invoiceCode,
          name: data.name !== undefined ? data.name : original.name,
          vats: data.vats
            ? String(data.vats)
                .split(",")
                .map((v: string) => parseFloat(v.trim()))
                .filter((n) => !isNaN(n))
            : original.vats,
          price:
            data.price !== undefined
              ? parseFloat(data.price)
              : original.price,
          _version: original._version, // Added _version
        };

        await API.graphql({
          query: updateInvoiceItemsCode,
          variables: { input: updateInput },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
      }
      showConfirm(`${singleName} has been updated successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function trash(id: string) {
    try {
      const original = await get(id);

      if (original) {
        const updateInput: any = {
          id: original.id,
          deleted: "1",
          _version: original._version, // Added _version
        };

        await API.graphql({
          query: updateInvoiceItemsCode,
          variables: { input: updateInput },
          authMode: true
            ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
            : GRAPHQL_AUTH_MODE.AWS_IAM,
        });
      }

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id))
      );

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err) {
      showError(err);
    }
  }

  async function bulkTrash(resourceIds: any) {
    let idsToDelete = resourceIds;

    // If no IDs provided (e.g. Delete All button clicked with no selection),
    // fetch all active items first
    if (!idsToDelete || idsToDelete.size === 0) {
      const allItems = await fetchAll({
        session,
        startIndex: 0,
        limit: 999999,
      } as ListingVariables);
      
      idsToDelete = new Set(allItems.map((item: any) => item.id));
    }

    for (let id of idsToDelete) {
      try {
        await trash(id);
      } catch (err: Error | any) {
        showError(err);
      }
    }

    // Update listing to remove deleted items
    dispatch(
      setListing(
        listing.filter((resource: any) => !idsToDelete.has(resource.id))
      )
    );

    showConfirm(
      `${idsToDelete.size} ${listingName} items has been moved to trash`
    );
  }

  async function remove(id: any) {
    try {
      await API.graphql({
        query: deleteInvoiceItemsCode,
        variables: { id: id },
        authMode: true
          ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
          : GRAPHQL_AUTH_MODE.AWS_IAM,
      });

      dispatch(
        setListing(listing.filter((resource: any) => resource.id !== id))
      );

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err);
    }
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "operaCode",
      numeric: false,
      disablePadding: false,
      label: "Opera Code",
    },
    {
      id: "invoiceCode",
      numeric: false,
      disablePadding: false,
      label: "Item Code",
    },
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "vats",
      numeric: false,
      disablePadding: false,
      label: "Taxes",
    },
    {
      id: "price",
      numeric: false,
      disablePadding: false,
      label: "Price",
    },
    {
      id: "createdByName",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = [
    "operaCode",
    "invoiceCode",
    "name",
    "vats",
    "price",
  ];

  const options: any[] = [{ label: "None", value: "-1" }];

  if (listing) {
    for (let option of listing) {
      options.push({
        label: `${option.operaCode} - ${option.invoiceCode}`,
        value: option.id,
      });
    }
  }

  async function ImportData(file: any, accountID: string, conceptID: string) {
    try {
      const reader = new FileReader();
      const XLSX = require("xlsx");

      reader.onload = async (e: any) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const sheetName = workbook.SheetNames[0];
          const workSheet = workbook.Sheets[sheetName];
          const json: any[] = XLSX.utils.sheet_to_json(workSheet);

          changeLoading(true);

          let createdCount = 0;
          for (const row of json) {
            const operaCode = row["Code From Source"];
            const invoiceCode = row["Map to Code"];
            const name = row["Source Name"];
            const price = row["price"];

            // Build vats array based on Vat 1, Vat 2, Vat 3 columns
            const vats: number[] = [];
            if (row["Vat 1"] && String(row["Vat 1"]).trim() !== "") {
              vats.push(14);
            }
            if (row["Vat 2"] && String(row["Vat 2"]).trim() !== "") {
              vats.push(12);
            }
            if (row["Vat 3"] && String(row["Vat 3"]).trim() !== "") {
              vats.push(1);
            }

            if (operaCode && invoiceCode) {
              const createInput: any = {
                operaCode: String(operaCode),
                invoiceCode: String(invoiceCode),
                name: name ? String(name) : null,
                vats: vats.length > 0 ? vats : null,
                price: price ? parseFloat(price) : null,
                deleted: "0",
                createdAt: new Date().getTime().toString(),
                createdByID: session.sub,
                createdByName: session.name,
              };

              await API.graphql({
                query: createInvoiceItemsCode,
                variables: { input: createInput },
                authMode: true
                  ? GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS
                  : GRAPHQL_AUTH_MODE.AWS_IAM,
              });
              createdCount++;
            }
          }

          changeLoading(false);
          showConfirm(`${createdCount} items imported successfully`);

          // Refresh listing
          // We can't easily call fetch here because it depends on params passed to it from the component
          // But we can update the reducer if we fetched everything, or just let the user refresh/navigate
          // To be safe and see results, let's try to fetch if we have filters, or just reload page?
          // Best practice in this codebase seems to be to let the user or caller handle refresh,
          // but ImportData in List.tsx doesn't seem to trigger a refresh automatically unless we do it here.
          // List.tsx doesn't await ImportData or do anything after it.

          // Let's attempt to reload the current view by dispatching a refresh or similar if possible.
          // Since we don't have the current pagination params here, we can't perfectly refetch.
          // However, we can trick it or just ask user to refresh.
          // For now, let's just show the confirm. The user can refresh.
          // actually, let's try to dispatch setListing with the new items appended if possible?
          // No, that's complex without the IDs returned.

          // Force a reload of the page after 1.5 seconds to show data?
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } catch (err: any) {
          changeLoading(false);
          console.error("Error parsing/uploading excel", err);
          showError("Error importing data: " + err.message);
        }
      };

      reader.readAsBinaryString(file);
    } catch (err: any) {
      console.error(err);
      showError(err.message);
    }
  }

  const api: any = {};

  api[`${listingName}Listing`] = listing;
  api[`${listingName}AllListing`] = listingAll;
  api[`${listingName}Options`] = options;
  api[`${listingName}SearchText`] = searchText;
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  // api[`${listingName}Model`] = InvoiceItemsCode; // Type not yet available
  api[`${listingName}Selected`] = selected;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}FetchAll`] = fetchAll;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ImportData`] = ImportData;
  api[`${listingName}ChangeListing`] = (listing: any[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeAllListing`] = (listing: any[]) =>
    dispatch(setListingAll(listing));
  api[`${listingName}Search`] = (searchText: string) =>
    dispatch(setSearchText(searchText));
  api[`${listingName}Select`] = (id: string) => dispatch(setSelected(id));

  return api;
};

export default useInvoiceItemsCode;
